// 분기 슬롯 탭 바 — 본문(BranchSlotTabsOverlay)과 작성 탭(WriteCanvasBranchTabsOverlay)이
// **한 벌로 공유**하는 표시·상호작용 단위. 두 캔버스가 같은 UI/UX를 갖는 게 확정 방침이라, 모양과
// 상호작용을 각자 두면 반드시 갈라진다(라벨 폴백만 서로 다르고 그건 호출부가 이미 계산해 넘긴다).
//
// ── 배치: 노드 위에 전체 슬롯을 상시 ──────────────────────────────────────────────
// 슬롯 탭은 자기 branch 노드 **바로 위**에 있어야 "이 노드의 분기"로 읽히고, **선택지가 전부 보여야**
// 무엇으로 바꿀 수 있는지 눈으로 바로 안다. 2026-08-31에 배치를 두 번 옮겨 봤다가 둘 다 되돌렸다:
//   - A4 박스 왼쪽 여백 열: 가림은 없지만 시선이 문서와 열 사이를 왕복해야 했다.
//   - 노드 위 컴팩트 칩(활성 슬롯만) + hover 확장: 가림은 한 줄로 줄었지만, 선택지를 보려면 마우스를
//     올려야 해서 "어떤 분기가 있는지"가 화면에서 사라졌다.
// 결론은 원래 방식 — 노드 위에 전체 슬롯 탭을 상시 표시한다. 바로 위 형제를 한 줄 가리는 것은
// 감수한 비용이고(그래서 바 높이는 낮게 유지한다), 줄이려면 배치가 아니라 lift·높이·톤을 만진다.

import type { CSSProperties } from 'react';
// 높이·상한은 배치 계산(기준 top 추정·줄 접힘)과 **같은 값**이어야 한다 — 갈라지면 첫 커밋 자리가
// 튀거나 예상과 다른 줄에서 접힌다. 그래서 기하 상수는 branchTabLayout이 정본.
import { TAB_BAR_MAX_WIDTH, TAB_HEIGHT } from './branchTabLayout';

/** 탭 글꼴 — 고정폭. 라벨 길이가 폭에 그대로 비례해 눈대중이 쉽고 줄 접힘이 예측 가능하다. */
const FONT: CSSProperties = {
  fontSize: 9,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

/**
 * 탭 1개 스타일. 활성=채움(teal-600), 비활성=외곽선.
 *
 * 흰 링(boxShadow)은 문서 위에 얹히는 배치라 필수다 — 표 테두리·글자 위에서 경계가 사라지지 않게.
 */
function tabStyle(isActive: boolean): CSSProperties {
  return {
    ...FONT,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0 5px',
    height: TAB_HEIGHT,
    lineHeight: 1,
    borderRadius: 3,
    border: isActive ? '1px solid #0d9488' : '1px solid #99f6e4',
    background: isActive ? '#0d9488' : '#f0fdfa',
    color: isActive ? '#fff' : '#0f766e',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    boxShadow: '0 0 0 1px #fff',
  };
}

/** 이 컴포넌트가 쓰는 두 실측 앵커 속성 — 본문/작성이 서로의 바를 재지 않게 이름을 나눈다. */
export type TabBarSurface = 'body' | 'write';

/** 실측 앵커 selector(호출부의 querySelector와 짝 — 이름이 갈라지지 않게 여기서 만든다). */
export function tabBarSelector(surface: TabBarSurface, measureKey: string): string {
  const attr = surface === 'body' ? 'data-branch-tab-bar' : 'data-write-branch-tab-bar';
  return `[${attr}="${measureKey.replace(/["\\]/g, '\\$&')}"]`;
}

export interface BranchSlotTabBarProps {
  /** 본문/작성 구분 — 실측 앵커 속성 이름만 가른다(모양·동작은 동일). */
  surface: TabBarSurface;
  /** 실측 앵커 값 + 오프셋 키. 본문은 branchId, 작성 탭은 `branchId#인스턴스순번`. */
  measureKey: string;
  /** 슬롯 탭 앵커(`data-slot-tab="<branchId>::<slotIndex>"`)에 쓰는 branch id. */
  branchId: string;
  /** 슬롯 탭 목록 — 라벨은 호출부가 각 surface의 폴백 규칙으로 계산해 넘긴다. */
  slots: ReadonlyArray<{ id: string; label: string }>;
  /** 현재 활성 슬롯 인덱스(범위 clamp는 호출부 책임). */
  activeIndex: number;
  /** 기준 위치(캔버스 상대좌표) — 겹침 오프셋이 이미 반영된 값. */
  top: number;
  left: number;
  /** 시뮬레이션 miss(조건 일치 데이터 없음 → 현장 값 폴백) — ⚠ 배지. 작성 탭엔 시뮬레이션이 없다. */
  missed?: boolean;
  onSelectSlot: (index: number) => void;
}

/**
 * 노드 위에 얹히는 슬롯 탭 바. 위치는 호출부가 실측·밀어내기로 정해 넘기고, 이 컴포넌트는 그 자리에
 * 무엇을 어떻게 그릴지만 정한다.
 */
export function BranchSlotTabBar({
  surface,
  measureKey,
  branchId,
  slots,
  activeIndex,
  top,
  left,
  missed,
  onSelectSlot,
}: BranchSlotTabBarProps) {
  return (
    <div
      // 겹침 실측 앵커(tabBarSelector) — 바의 폭·높이를 재는 자리. surface마다 이름이 다르다.
      data-branch-tab-bar={surface === 'body' ? measureKey : undefined}
      data-write-branch-tab-bar={surface === 'write' ? measureKey : undefined}
      // 기본 반투명 + hover 불투명(2026-07-03 도입 → 배치 실험 중 유실, 08-31 복원) — 바가 자기
      // 노드 바로 위 콘텐츠를 상시 가리는 문제를 **레이아웃을 건드리지 않고** 완화한다. 투명도라
      // rect·앵커·겹침 계산은 그대로이고(측정은 opacity와 무관), 읽을 때는 흐릿하게 비켜 있다가
      // 만지려고 다가가면 또렷해진다. CSS :hover라 JS 상태가 없다 — 리렌더도 재측정도 없다.
      className="opacity-40 transition-opacity hover:opacity-100"
      style={{
        position: 'absolute',
        top,
        left,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        // 바가 가로로 길게 문서를 덮지 않도록 상한에서 줄을 접는다(늘어난 높이는 밀어내기가 흡수).
        maxWidth: TAB_BAR_MAX_WIDTH,
        pointerEvents: 'auto',
        // F9 ▸ 표식(zIndex 27)이 탭 위에 남는 층. 바끼리의 가림은 층이 아니라 밀어내기가 없앤다
        // (resolveTabBarOverlaps).
        zIndex: 26,
      }}
    >
      {slots.map((slot, i) => (
        <button
          key={slot.id}
          type="button"
          // 분기 관계 연결선(BranchLinkOverlay)의 **출발점 앵커**. 그 오버레이는 이 탭 rect에서
          // 중첩 branch 노드 rect로 선을 긋고, 비활성 탭에는 ▸ 표식을 붙인다. 실측이라 밀어내기로
          // 바가 내려가도 선이 따라온다(앵커 규약 불변).
          data-slot-tab={`${branchId}::${i}`}
          // 활성 슬롯을 **색으로만** 알리지 않는다. tab/tablist가 아니라 `aria-pressed`인 이유는
          // 이게 실제로 tab 패턴이 아니기 때문이다 — 짝이 되는 tabpanel이 없고(전환되는 것은
          // 캔버스 문서 자체다) 바가 여럿 떠도 서로 독립이라 tablist의 단일 활성 계약이 성립하지
          // 않는다. "지금 이 슬롯으로 보고 있다"는 토글 버튼의 눌림 상태가 실물에 맞다.
          aria-pressed={i === activeIndex}
          title={`슬롯 ${i + 1} — ${slot.label}`}
          onClick={(e) => {
            e.stopPropagation();
            onSelectSlot(i);
          }}
          style={tabStyle(i === activeIndex)}
        >
          {slot.label}
        </button>
      ))}
      {/* 시뮬레이션 miss 배지 — 활성 슬롯 조건에 일치하는 데이터가 없어 현장 값으로 표시 중. */}
      {missed && (
        <span
          title="조건 일치 데이터 없음 — 현장 값으로 표시"
          style={{
            color: '#b45309',
            fontSize: 10,
            lineHeight: `${TAB_HEIGHT}px`,
            userSelect: 'none',
            textShadow: '0 0 2px #fff, 0 0 2px #fff',
          }}
        >
          ⚠
        </span>
      )}
    </div>
  );
}
